import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TwoPaneLayout } from '../components/TwoPaneLayout';
import { StoreBadges } from '../components/StoreBadges';
// GoogleSignInButton import intentionally removed — see comment block
// below the Create-account button for the rationale. Restore from
// '../components/GoogleSignInButton' if reinstating.
import {
  ApiError,
  login,
  register,
  resendVerification,
  verifyEmail,
} from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import PasswordField from '../components/PasswordField';

/**
 * Self-serve signup — two-step flow mirroring the iOS app's
 * OTPVerificationView pattern.
 *
 * STEP 1 (form): user enters name/email/password → POST /auth/register
 * → server creates user with email_verified=false, mints a 6-char OTP,
 * sends it via the configured mailer. Response carries no accessToken
 * because the user isn't verified yet.
 *
 * STEP 2 (verify): user enters the 6-char code → POST /auth/verify-email
 * → server flips email_verified=true. We then call login() with the
 * original credentials to mint a session and land the user on
 * /dashboard authed.
 *
 * The invite-driven flow lives in `InviteLanding.tsx` — that path also
 * goes through /auth/register but follows up with aquastarInviteAccept
 * to join the inviter's pump fleet. (When invite-landing gets refactored
 * to match this two-step pattern, the verify-step UI in this file
 * should be extracted into a shared component.)
 *
 * Soft-409 handling: if the email already exists but is unverified,
 * the server re-mints a code and we move straight to step 2 (so the
 * user can complete a previously-abandoned signup without retyping).
 * Hard 409 (already verified) bounces to the "sign in instead" hint.
 */
export function Signup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  // Step gate. 'form' = enter credentials; 'verify' = enter OTP.
  const [step, setStep] = useState<'form' | 'verify'>('form');

  // Step 1 fields
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2 fields
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);

  // ── Step 1 — credentials form ────────────────────────────────────

  function validateForm(): string | null {
    if (!email) return 'Please enter your email.';
    if (!password) return 'Please enter a password.';
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (password !== confirmPassword) return "Passwords don't match.";
    return null;
  }

  async function onSubmitForm(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const v = validateForm();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const resp = await register(email, password, displayName.trim() || undefined);
      // Both 'OK' (new user) and 'PENDING_VERIFICATION' (re-issued code
      // for unverified existing account) land us at step 2.
      if (resp.code === 'PENDING_VERIFICATION') {
        setVerifyNotice(
          "We've sent a new code — your previous signup was never verified.",
        );
      } else {
        setVerifyNotice(null);
      }
      setStep('verify');
    } catch (err) {
      // Hard 409: already exists AND verified → bounce to sign-in hint.
      if (err instanceof ApiError && err.status === 409) {
        setError(
          'An account with that email already exists. Please sign in instead.',
        );
      } else if (err instanceof ApiError && err.status === 400) {
        // Password-policy / validation — server message is user-facing.
        setError(err.message);
      } else if (err instanceof ApiError && err.status === 403) {
        // Server-side self-registration disabled. Tell the user clearly
        // (instead of pretending we tried something). Same wording the
        // server uses so support tickets quoting either match.
        setError(
          'Self-registration is disabled on this instance. Please contact an administrator for an invite.',
        );
      } else {
        setError(
          err instanceof Error ? err.message : 'Sign-up failed. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step 2 — OTP verification ────────────────────────────────────

  function validateOtp(): string | null {
    const digits = otp.replace(/\D/g, '');
    if (digits.length === 0) return 'Enter the code from your email.';
    if (digits.length !== 6) return 'The code is 6 digits.';
    return null;
  }

  async function onSubmitVerify(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const v = validateOtp();
    if (v) {
      setError(v);
      return;
    }
    setVerifying(true);
    try {
      await verifyEmail(email, otp.replace(/\D/g, ''));
      // Verified — now establish a session with the original credentials.
      await login(email, password);
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // Wrong-or-expired code, or anti-enum unknown-email. Same copy
        // either way per server design.
        setError('That code is invalid or expired. Try again or resend.');
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Verification failed. Please try again.',
        );
      }
    } finally {
      setVerifying(false);
    }
  }

  async function onResend() {
    setResendBusy(true);
    setError(null);
    try {
      await resendVerification(email);
      setVerifyNotice("If your email is registered, a new code is on its way.");
    } catch (err) {
      // Server returns 200 even on internal errors (anti-enum), so any
      // exception we see here is a network problem.
      setError(
        err instanceof Error
          ? err.message
          : 'Resend failed. Please check your connection.',
      );
    } finally {
      setResendBusy(false);
    }
  }

  function onBackToForm() {
    setStep('form');
    setOtp('');
    setError(null);
    setVerifyNotice(null);
  }

  // ── Render ───────────────────────────────────────────────────────

  if (step === 'verify') {
    return (
      <TwoPaneLayout>
        <form onSubmit={onSubmitVerify} noValidate>
          <h2 className="h4 mb-3">Verify your email</h2>
          <p className="text-muted small mb-2">
            We sent a 6-digit code to:
          </p>
          <p className="fw-semibold mb-3">{email}</p>
          <p className="text-muted small mb-4">
            Be sure to check your spam folder.
          </p>

          {verifyNotice && (
            <div className="alert alert-info" role="status">
              {verifyNotice}
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small text-muted">Verification code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="form-control"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              autoFocus
              required
            />
            <div className="form-text">Enter the 6-digit code from the email.</div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mb-3"
            disabled={verifying || otp.length !== 6}
          >
            {verifying ? 'Verifying…' : 'Verify and sign in'}
          </button>

          <div className="d-flex justify-content-between small">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={onResend}
              disabled={resendBusy}
            >
              {resendBusy ? 'Resending…' : 'Resend code'}
            </button>
            <button
              type="button"
              className="btn btn-link p-0 text-muted"
              onClick={onBackToForm}
            >
              Back to sign up
            </button>
          </div>
        </form>
      </TwoPaneLayout>
    );
  }

  return (
    <TwoPaneLayout>
      <form onSubmit={onSubmitForm} noValidate>
        <h2 className="h4 mb-3">Create your AquaStar account</h2>
        <p className="text-muted small mb-4">
          Manage your pool pump from anywhere. Already have an account?{' '}
          <Link to="/login">Sign in</Link>.
        </p>

        <div className="mb-3">
          <label className="form-label small text-muted">
            Name <span className="text-muted">(optional)</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            placeholder="What should we call you?"
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Email</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Password</label>
          <PasswordField
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className="form-text">At least 8 characters.</div>
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Confirm password</label>
          <PasswordField
            className="form-control"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100 mb-3"
          disabled={submitting || !email || !password || !confirmPassword}
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>

        {/*
          Google OAuth intentionally NOT rendered here. The OAuth
          callback in Nexus auto-creates a user when no row matches
          the Google email (core/auth/oauth/service.ts attachOrCreateUser
          step 3), so "Sign in with Google" on the Login page serves
          both first-time signup and returning sign-in. Duplicating
          the button here invites the failure mode where a user clicks
          "Sign up with Google", gets an account via OAuth, then waits
          for a verification email that never arrives because OAuth
          bypasses the OTP step. Removed 2026-05-21 per Erwin's call;
          matches the parallel iOS SignUpView change (commit 3daf8a6).
        */}

        <hr className="my-4" />
        <p className="small text-muted text-center mb-2">
          Or get the AquaStar app:
        </p>
        <StoreBadges />
      </form>
    </TwoPaneLayout>
  );
}
